import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../../../context/I18nContext';
import { MenuList } from './Menu';
import { findMenuTrail, resolveMenuPath } from './menu-trail';
import './page-location.css';

/** Tam ekran yuksekligine gore cizilen sayfalar; ekstra satir yazma kutusunu ekran disina iterdi. */
const HIDDEN_ON_PATHS = new Set(['/messages']);

/**
 * Sayfa basliginin ustundeki konum satiri (Yonlendirme Faz 3.2):
 * "Anasayfa › Gorusme & Toplanti › Goruseceğim Firmalar". Metin menuden
 * gelir; anasayfada ve menude olmayan sayfalarda gosterilmez.
 */
const PageLocation = () => {
    const { t } = useI18n();
    const { pathname, search } = useLocation();
    const trail = HIDDEN_ON_PATHS.has(pathname) ? null : findMenuTrail(MenuList, pathname, search);
    if (!trail || trail[trail.length - 1] === 'menu.dashboard') return null;
    // Detay sayfasinda ust sayfanin adi listeye geri donen baglanti olur.
    const { menuPath, detailTitleKey } = resolveMenuPath(pathname);
    const parentIndex = detailTitleKey ? trail.length - 2 : -1;

    return (
        <nav className="wc-page-location" aria-label={t('pageLocation.ariaLabel')}>
            <ol>
                <li>
                    <Link to="/home">{t('menu.dashboard')}</Link>
                </li>
                {trail.map((titleKey, index) => (
                    <li key={titleKey} aria-current={index === trail.length - 1 ? 'page' : undefined}>
                        {index === parentIndex ? <Link to={`/${menuPath}`}>{t(titleKey)}</Link> : t(titleKey)}
                    </li>
                ))}
            </ol>
        </nav>
    );
};

/**
 * Ortak sayfa basligi (PageHeader) konum satirini kendi kartinin icinde
 * cizer. Sayfada boyle bir baslik varsa yerlesimdeki satir gizlenir; yoksa
 * (Etkinlikler gibi ozel sayfalar) yerlesim cizmeye devam eder.
 */
type ClaimPageLocation = (claimed: boolean) => void;

const PageLocationClaimContext = createContext<ClaimPageLocation | null>(null);

export const PageLocationArea = ({ children }: { children: React.ReactNode }) => {
    const [claimCount, setClaimCount] = useState(0);
    const claim = useCallback<ClaimPageLocation>((claimed) => {
        setClaimCount((count) => count + (claimed ? 1 : -1));
    }, []);

    return (
        <PageLocationClaimContext.Provider value={claim}>
            {claimCount === 0 ? <PageLocation /> : null}
            {children}
        </PageLocationClaimContext.Provider>
    );
};

/** Konum satirini kendi icinde cizecek bileseni bildirir; yerlesimin icindeyse `true`. */
export const useClaimPageLocation = () => {
    const claim = useContext(PageLocationClaimContext);
    // Boyamadan once: yerlesimdeki satir bir an bile gorunup kaybolmasin.
    useLayoutEffect(() => {
        if (!claim) return undefined;
        claim(true);
        return () => claim(false);
    }, [claim]);
    return Boolean(claim);
};

export default PageLocation;
